import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Send, Megaphone } from "lucide-react";
import { format } from "date-fns";

type Audience = "all" | "role" | "org";

interface BroadcastRow {
  id: string;
  title: string;
  body: string;
  audience_kind: string;
  audience_role: string | null;
  audience_org_id: string | null;
  recipient_count: number;
  sent_at: string | null;
  created_at: string;
}

interface OrgRow {
  id: string;
  name: string;
}

export default function AdminNotifications() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [audience, setAudience] = useState<Audience>("all");
  const [audienceRole, setAudienceRole] = useState<string>("user");
  const [audienceOrg, setAudienceOrg] = useState<string>("");
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [history, setHistory] = useState<BroadcastRow[]>([]);
  const [sending, setSending] = useState(false);

  const loadHistory = async () => {
    const { data } = await supabase
      .from("notification_broadcasts")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);
    setHistory((data ?? []) as BroadcastRow[]);
  };

  const loadOrgs = async () => {
    const { data } = await supabase.from("organizations").select("id, name").is("deleted_at", null);
    setOrgs((data ?? []) as OrgRow[]);
  };

  useEffect(() => {
    loadHistory();
    loadOrgs();
  }, []);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    if (audience === "role" && !audienceRole) {
      toast.error("Pick a role");
      return;
    }
    if (audience === "org" && !audienceOrg) {
      toast.error("Pick an organization");
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-broadcast", {
        body: {
          title: title.trim(),
          body: body.trim(),
          link: link.trim() || undefined,
          audience_kind: audience,
          audience_role: audience === "role" ? audienceRole : undefined,
          audience_org_id: audience === "org" ? audienceOrg : undefined,
        },
      });
      if (error) throw error;
      toast.success(
        `Broadcast sent to ${data?.recipients ?? 0} user${data?.recipients === 1 ? "" : "s"} (${data?.sent ?? 0} push delivered)`,
      );
      setTitle("");
      setBody("");
      setLink("");
      loadHistory();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message ?? "Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-5xl">
      <div className="flex items-center gap-2">
        <Megaphone className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Broadcast Notifications</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Compose new broadcast</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="body">Message</Label>
            <Textarea id="body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={500} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="link">Link (optional)</Label>
            <Input
              id="link"
              placeholder="/student/dashboard"
              value={link}
              onChange={(e) => setLink(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Audience</Label>
              <Select value={audience} onValueChange={(v) => setAudience(v as Audience)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone</SelectItem>
                  <SelectItem value="role">By user role</SelectItem>
                  <SelectItem value="org">By organization</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {audience === "role" && (
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={audienceRole} onValueChange={setAudienceRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Students</SelectItem>
                    <SelectItem value="parent">Parents</SelectItem>
                    <SelectItem value="instructor">Instructors</SelectItem>
                    <SelectItem value="admin">Admins</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {audience === "org" && (
              <div className="space-y-2">
                <Label>Organization</Label>
                <Select value={audienceOrg} onValueChange={setAudienceOrg}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Button onClick={handleSend} disabled={sending} className="w-full md:w-auto">
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Sending…" : "Send now"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent broadcasts</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No broadcasts sent yet.</div>
          ) : (
            <div className="space-y-3">
              {history.map((h) => (
                <div key={h.id} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{h.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {h.sent_at ? format(new Date(h.sent_at), "PPp") : "Pending"}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{h.body}</div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Audience: <span className="font-medium">{h.audience_kind}</span>
                    {h.audience_role && ` (${h.audience_role})`} · Reached{" "}
                    <span className="font-medium">{h.recipient_count}</span> user
                    {h.recipient_count === 1 ? "" : "s"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
